/**
 * @file VideoSelectionPage.tsx
 *
 * @description A React component that provides an interface for police officers to browse, filter, select,
 * and process bodycam video evidence. It supports pagination, date filtering, search functionality,
 * and video selection management.
 *
 * @module Views
 */
import React, { useState } from "react";
import { Box, Container, Tab, Tabs, alpha, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "../../context/LayoutContext";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { VideoFilters } from "./components/VideoFilters";
import { SelectionControls } from "./components/SelectionControls";
import { InstructionBanner } from "./components/InstructionBanner";
import { VideoGrid } from "./components/VideoGrid";
import { EvidenceTab, useListVideos } from "./hooks/useListVideos";
import { useVideoSelection } from "./hooks/useVideoSelection";

const VideoSelectionPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, setIsNavigating] = useState(false);
  const [activeTab, setActiveTab] = useState<EvidenceTab>("ingested");

  // Data fetching and state
  const {
    videos,
    isLoading,
    isInitialLoad,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sortBy,
    setSortBy,
    fetchVideos,
    markVideoFetching,
    loadMore,
    hasMore,
    searchTerm,
    setSearchTerm,
    handleSearchChange,
  } = useListVideos(activeTab);

  // Selection and filtering state
  const {
    selectedVideos,
    handleVideoSelection,
    handleRemoveVideo,
    clearSelection,
  } = useVideoSelection();

  // Set page header in app bar
  usePageHeader({
    title: "Video Evidence Locker",
    subtitle:
      "Browse, filter, and select bodycam footage for report generation",
    icon: <VideoLibraryIcon />,
    chip: {
      label:
        activeTab === "unfetched"
          ? `${videos.length} Not Fetched`
          : `${videos.length} Videos`,
    },
  });

  // Actions
  const handleResetFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setSearchTerm("");
    setSortBy("createdAt");
  };

  /**
   * @name handleProcessVideos
   *
   * @description Processes selected videos and navigates to the report generation page;
   * For multiple videos, IDs are joined with a tilde (~) in the URL
   */
  const handleProcessVideos = async () => {
    if (selectedVideos.length === 0) return;
    try {
      setIsNavigating(true);
      if (selectedVideos.length === 1) {
        navigate(`/generate-report/${selectedVideos[0]}`);
      } else {
        const videoIdsPath = selectedVideos.join("~");
        navigate(`/generate-report/${videoIdsPath}`);
      }
    } catch (error) {
      console.error("Error navigating with videos:", error);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <Container
      sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto", py: 2 }}
    >
      <Grid
        container
        spacing={3}
        direction="column"
        sx={{
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {/* Filters Section */}
        <VideoFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onReset={handleResetFilters}
          disabled={activeTab === "unfetched"}
        />

        {/* Ingested vs. not-fetched (AI-filtered) evidence tabs */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v as EvidenceTab)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                value="ingested"
                label="Ingested"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                value="unfetched"
                label="Not Fetched"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>
          </Box>
        </Grid>

        {/* Selection and upload only apply to ingested evidence */}
        {activeTab === "ingested" && (
          <>
            {/* Selected videos and actions */}
            <SelectionControls
              selectedVideos={selectedVideos}
              allVideos={videos}
              onRemoveVideo={handleRemoveVideo}
              onClearSelection={clearSelection}
              onProcessVideos={handleProcessVideos}
              fetchVideos={fetchVideos}
              allowUpload={true}
            />

            {/* Instruction Banner */}
            <InstructionBanner />
          </>
        )}

        {/* Video cards display grid; keyed by tab so the grid's load state and
            infinite-scroll sentinel reset when switching tabs */}
        <VideoGrid
          key={activeTab}
          tab={activeTab}
          isLoading={isLoading || isInitialLoad}
          paginatedVideos={videos}
          selectedVideos={selectedVideos}
          onVideoSelect={handleVideoSelection}
          hasMore={hasMore}
          onLoadMore={loadMore}
          hasActiveFilters={
            activeTab === "ingested" && (!!dateFrom || !!dateTo || !!searchTerm)
          }
          onResetFilters={handleResetFilters}
          onFetchRequested={markVideoFetching}
        />
      </Grid>
    </Container>
  );
};

export default VideoSelectionPage;
